import { GridColumnHeader } from '../components/Grid/types';
import type { Attribute, DataSource } from '../types/entities.types';
import { getSearchableFields } from '../utils/searchable.utils';

const gridHeaders: GridColumnHeader[] = [
  {
    name: 'businessMetadata.asset_name',
    label: 'Asset Name',
    width: '20%',
    filter: {
      type: 'text',
      remoteSearchFields: ['asset_name'],
    },
    sortable: true,
    remoteSortField: 'asset_name',
  },
  {
    name: 'technicalMetadata.env',
    label: 'Environment',
    width: '8%',
    filter: {
      type: 'aggregation',
      aggregationName: 'env_aggregation',
      remoteSearchFields: ['env'],
      filterSelectProps: {
        isClearable: true,
      },
    },
    remoteSortField: 'env',
  },
  {
    name: 'businessMetadata.descriptiveMetadata.description',
    label: 'Description',
    width: '22%',
  },
  {
    name: 'sourceSpecificMetadata.UnityCatalog.catalog_name',
    label: 'Catalog name',
    width: '20%',
  },
  {
    name: 'sourceSpecificMetadata.UnityCatalog.schema_name',
    label: 'Schema name',
    width: '20%',
  },
  { name: 'lineage', label: 'Lineage', width: '10%' },
];

const attributesMap: Attribute[] = [
  {
    name: 'businessMetadata.asset_id',
    label: 'Asset Id',
    showInColumn: 0,
    description: 'Asset id',
  },
  {
    name: 'businessMetadata.asset_name',
    label: 'Asset Name',
    showInColumn: 0,
    description: 'Asset name',
  },
  {
    name: 'sourceSpecificMetadata.UnityCatalog.table_id',
    label: 'Table Id',
    showInColumn: 0,
    description: 'Table id',
  },
  {
    name: 'sourceSpecificMetadata.UnityCatalog.table_name',
    label: 'Table Name',
    showInColumn: 0,
    description: 'Table name',
  },
  {
    name: 'technicalMetadata.env',
    label: 'Environment',
    showInColumn: 0,
    description: 'Asset environment',
  },
  {
    name: 'sourceSpecificMetadata.UnityCatalog.storage_path',
    label: 'Storage Path',
    showInColumn: 0,
    description: 'Storage path',
  },
  {
    name: 'technicalMetadata.asset_type',
    label: 'Asset Type',
    showInColumn: 0,
    description: 'Asset type',
  },
  {
    name: 'technicalMetadata.asset_age',
    label: 'Asset Age',
    showInColumn: 0,
    description: 'Asset age',
    suffix: ' days',
  },
  {
    name: 'technicalMetadata.created_time',
    label: 'Created Time',
    showInColumn: 0,
    description: 'Created time',
    type: 'date',
  },
  {
    name: 'technicalMetadata.created_by',
    label: 'Created By',
    showInColumn: 0,
    description: 'Created by',
  },
  {
    name: 'technicalMetadata.usageMetadata.last_altered_age',
    label: 'Last Altered Age',
    showInColumn: 1,
    description: 'Last altered age',
    suffix: ' days',
  },
  {
    name: 'technicalMetadata.usageMetadata.last_altered_by',
    label: 'Last Altered By',
    showInColumn: 1,
    description: 'Last altered by',
  },
  {
    name: 'technicalMetadata.usageMetadata.last_altered',
    label: 'Last Altered Date',
    showInColumn: 1,
    description: 'Last altered date',
    type: 'date',
  },
  {
    name: 'businessMetadata.descriptiveMetadata.description',
    label: 'Description',
    showInColumn: 2,
    description: 'Asset description',
  },
  {
    name: 'sourceSpecificMetadata.UnityCatalog.metastore_id',
    label: 'Metastore Id',
    showInColumn: 1,
    description: 'Metastore id',
  },
  {
    name: 'sourceSpecificMetadata.UnityCatalog.catalog_name',
    label: 'Table Catalog',
    showInColumn: 1,
    description: 'Table catalog',
    isSearchable: true,
  },
  {
    name: 'sourceSpecificMetadata.UnityCatalog.schema_name',
    label: 'Table Schema',
    showInColumn: 1,
    description: 'Table schema',
    isSearchable: true,
  },
  {
    name: 'sourceSpecificMetadata.UnityCatalog.is_insertable_into',
    label: 'Is Insertable Into',
    showInColumn: 1,
    type: 'boolean',
    description: 'Is insertable into',
  },
  {
    name: 'sourceSpecificMetadata.UnityCatalog.format',
    label: 'Format',
    showInColumn: 1,
    description: 'Format',
  },
  {
    name: 'sourceSpecificMetadata.UnityCatalog.properties',
    label: 'Properties',
    showInColumn: 1,
    description: 'Properties',
    type: 'table',
    tableColumns: ['Name', 'Value'],
    tableFieldMapping: {
      Name: 'name',
      Value: 'value',
    },
  },
  {
    name: 'sourceSpecificMetadata.UnityCatalog.tags',
    label: 'Tags',
    showInColumn: 2,
    type: 'table',
    tableColumns: ['Name', 'Value'],
    tableFieldMapping: {
      Name: 'name',
      Value: 'value',
    },
  },
  {
    name: 'businessMetadata.administrativeMetadata.steward',
    label: 'Steward',
    showInColumn: 2,
    description: 'Steward',
  },
  {
    name: 'businessMetadata.administrativeMetadata.technical_data_steward',
    label: 'Technical Data Steward',
    showInColumn: 2,
    description: 'Technical Data Steward',
  },
  {
    name: 'sourceSpecificMetadata.UnityCatalog.clustering_columns',
    label: 'Clustering Columns',
    type: 'commaSeparated',
    showInColumn: 1,
    description: 'Clustering columns',
  },
  {
    name: 'businessMetadata.descriptiveMetadata.description',
    label: 'Technical Description',
    description: 'Technical Description',
  },
];

const customQueryForListPage = `
  UnityCatalog {
    metastore_id
    is_insertable_into
    format
    storage_path
    table_id
    table_name
    catalog_name
    schema_name
    clustering_columns
  }
`;

const customQueryForDetailsPage = `
  UnityCatalog {
    metastore_id
    is_insertable_into
    format
    storage_path
    table_id
    table_name
    catalog_name
    schema_name
    clustering_columns
    tags {
      name
      value
    }
    properties {
      name
      value
    }
    partition_columns {
      columnName
      description
      dataType
    }
    columns {
      columnName
      description
      businessDescription
      dataType
      defaultValue
      nullable
      dataTypeDetails
      tags {
        name
        value
      }
    }
  }
`;

const searchableFields = [...getSearchableFields(attributesMap), 'Column Names'];

export const UnityCatalogConfig: DataSource = {
  id: '7',
  name: 'UnityCatalog',
  showColumns: true,
  showPartitionColumns: true,
  showLineage: true,
  showLineageTable: true,
  showLineagePipeline: false,
  showLineageQuery: false,
  showLineageCloned: false,
  showLineageCW: false,
  showLineageColumn: true,
  showLineageDashboard: false,
  gridHeaders,
  attributesMap,
  customQueryForListPage,
  customQueryForDetailsPage,
  searchableFields,
};
